import { useNavigate } from 'react-router-dom'
import {
  Sliders,
  CreditCard,
  FileText,
  Terminal,
  Cpu,
  Users,
  Activity,
  FileCode,
  Puzzle,
  Shield,
  Globe,
  Sparkles,
  Gift,
} from 'lucide-react'
import { getApiBase } from '../lib/utils'

interface AdminTile {
  name: string
  description: string
  href: string
  icon: React.ElementType
  color: string
  external?: boolean
}

const adminTiles: AdminTile[] = [
  {
    name: 'Métriques & Performance',
    description: 'Statistiques de la plateforme et métriques agrégées',
    href: '/admin/metrics',
    icon: Activity,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    name: 'Config Globale',
    description: 'Paramètres généraux de la plateforme, branding, emails',
    href: '/global-config',
    icon: Sliders,
    color: 'from-violet-500 to-purple-600',
  },
  {
    name: 'Plans & Tarifs',
    description: 'Gérer les plans d\'abonnement et synchroniser avec Stripe',
    href: '/admin/plans',
    icon: CreditCard,
    color: 'from-amber-500 to-orange-600',
  },
  {
    name: 'Utilisateurs',
    description: 'Gérer les comptes utilisateurs et leurs permissions',
    href: '/admin/users',
    icon: Users,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    name: 'Affiliation',
    description: 'Commissions parrainage, export CSV, marquage payé',
    href: '/admin/affiliate',
    icon: Gift,
    color: 'from-rose-500 to-pink-600',
  },
  {
    name: 'Modèles IA',
    description: 'Configurer les modèles Ollama disponibles',
    href: '/models',
    icon: Cpu,
    color: 'from-amber-500 to-orange-600',
  },
  {
    name: 'Monitoring',
    description: 'Surveiller les performances et l\'état du système',
    href: '/monitoring',
    icon: Activity,
    color: 'from-cyan-500 to-sky-600',
  },
  {
    name: 'Logs Système',
    description: 'Consulter les journaux d\'activité de l\'API',
    href: '/logs',
    icon: FileText,
    color: 'from-slate-500 to-zinc-600',
  },
  {
    name: 'Logs MCP',
    description: 'Journaux des appels aux serveurs MCP',
    href: '/logs-mcp',
    icon: Terminal,
    color: 'from-fuchsia-500 to-purple-600',
  },
  {
    name: 'Pool de Proxies',
    description: 'Gérer les proxies HTTP pour les serveurs MCP (Click&Boat) — rotation automatique',
    href: '/admin/proxies',
    icon: Globe,
    color: 'from-teal-500 to-cyan-600',
  },
  {
    name: 'Guide de démarrage',
    description: 'Activer ou désactiver le guide de démarrage pour tous les utilisateurs',
    href: '/admin/onboarding',
    icon: Sparkles,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    name: 'API Docs',
    description: 'Documentation interactive de l\'API (Swagger)',
    href: '/api/docs',
    icon: FileCode,
    color: 'from-green-500 to-emerald-600',
    external: true,
  },
]

export default function AdminDashboardPage() {
  const navigate = useNavigate()

  const handleTileClick = (tile: AdminTile) => {
    if (tile.external) {
      window.open(`${getApiBase().replace('/api', '')}${tile.href}`, '_blank')
    } else {
      navigate(tile.href)
    }
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background gradient */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/30">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">
                Administration
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Gérez votre plateforme TotemAI
              </p>
            </div>
          </div>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminTiles.map((tile) => (
            <button
              key={tile.name}
              onClick={() => handleTileClick(tile)}
              className="group relative rounded-3xl border border-emerald-100/70 bg-white/90 p-6 text-left shadow-xl shadow-emerald-500/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-emerald-500/20 dark:border-emerald-900/50 dark:bg-zinc-950/90"
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${tile.color} opacity-0 transition-opacity duration-300 group-hover:opacity-5`} />
              
              {/* Icon */}
              <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${tile.color} shadow-lg`}>
                <tile.icon className="h-7 w-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                {tile.name}
                {tile.external && (
                  <span className="ml-2 text-xs text-zinc-400 dark:text-zinc-500">↗</span>
                )}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {tile.description}
              </p>

              {/* Arrow indicator */}
              <div className="absolute bottom-6 right-6 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 transition-all duration-300 group-hover:bg-emerald-500 group-hover:text-white dark:bg-zinc-800 dark:text-zinc-500">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>

        {/* Footer info */}
        <div className="mt-8 text-center text-sm text-zinc-400 dark:text-zinc-500">
          <p>Accès réservé aux administrateurs</p>
        </div>
      </div>
    </div>
  )
}
